import { useCallback, useMemo, memo, useRef, useState, useEffect, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { Sparkles, Plus, Eye, EyeOff, Search } from 'lucide-react'
import { useShallow } from 'zustand/react/shallow'
import { Button } from '@/components/ui/button'
import type { TimelineItem } from '@/types/timeline'
import type { ItemEffect, GpuEffect } from '@/types/effects'
import { EFFECT_PRESETS } from '@/types/effects'
import { useKeyframesStore, useTimelineStore } from '@/features/effects/deps/timeline-contract'
import { useGizmoStore, useThrottledFrame } from '@/features/effects/deps/preview-contract'
import { PropertySection } from '@/shared/ui/property-controls'
import { GpuEffectPanel, GpuWheelsPanel, GpuCurvesPanel } from './panels'
import { translateEffectUiText } from './panels/effect-ui-i18n'
import {
  getGpuCategoriesWithEffects,
  getGpuEffect,
  getGpuEffectDefaultParams,
} from '@/infrastructure/gpu/effects'
import { EffectThumbnail } from './effect-thumbnail'
import { prewarmEffectPreviews } from './effect-thumbnail/engine'
import { getMappedSelectionEffectEntry } from '../utils/effect-selection'
import {
  getAutoKeyframeOperation,
  getResolvedAnimatedEffectParamValue,
} from '@/features/effects/deps/keyframes-contract'
import { buildEffectAnimatableProperty, type AnimatableProperty } from '@/types/keyframe'

interface EffectsSectionProps {
  items: TimelineItem[]
  hiddenGpuEffectTypes?: readonly string[]
  visibleGpuEffectTypes?: readonly string[]
  layout?: 'sidebar' | 'dock'
  /**
   * Jump to the Color workspace. When provided, heavyweight grade panels
   * (wheels, curves) render collapsed in the sidebar with a "进入调色工作区"
   * affordance instead of the full grading surface.
   */
  onEditInColor?: () => void
}

const COLOR_GRADE_DOCK_EFFECT_TYPES = ['gpu-color-wheels', 'gpu-curves'] as const

const GPU_EFFECT_CATEGORY_LABELS: Record<string, string> = {
  color: '颜色',
  stylize: '风格化',
  distort: '扭曲',
  blur: '模糊',
  keying: '抠像',
}

function formatGpuEffectCategory(category: string): string {
  return GPU_EFFECT_CATEGORY_LABELS[category] ?? category.charAt(0).toUpperCase() + category.slice(1)
}

function matchesGpuEffectFilters(
  gpuEffectType: string,
  visibleGpuEffectTypes?: readonly string[],
  hiddenGpuEffectTypes?: readonly string[],
): boolean {
  if (visibleGpuEffectTypes && !visibleGpuEffectTypes.includes(gpuEffectType)) {
    return false
  }

  if (hiddenGpuEffectTypes?.includes(gpuEffectType)) {
    return false
  }

  return true
}

export const EffectsSection = memo(function EffectsSection({
  items,
  hiddenGpuEffectTypes,
  visibleGpuEffectTypes,
  layout = 'sidebar',
  onEditInColor,
}: EffectsSectionProps) {
  const addEffect = useTimelineStore((s) => s.addEffect)
  const addEffects = useTimelineStore((s) => s.addEffects)
  const updateEffect = useTimelineStore((s) => s.updateEffect)
  const removeEffect = useTimelineStore((s) => s.removeEffect)
  const toggleEffect = useTimelineStore((s) => s.toggleEffect)
  const applyAutoKeyframeOperations = useTimelineStore((s) => s.applyAutoKeyframeOperations)

  const setEffectsPreviewNew = useGizmoStore((s) => s.setEffectsPreviewNew)
  const clearPreview = useGizmoStore((s) => s.clearPreview)
  const currentFrame = useThrottledFrame()
  const visualItems = items
  const itemIds = useMemo(() => visualItems.map((item) => item.id), [visualItems])

  const effects = useMemo<ItemEffect[]>(
    () =>
      (visualItems[0]?.effects ?? []).filter((effect) => {
        if (effect.effect.type !== 'gpu-effect') {
          return false
        }

        return matchesGpuEffectFilters(
          effect.effect.gpuEffectType,
          visibleGpuEffectTypes,
          hiddenGpuEffectTypes,
        )
      }),
    [hiddenGpuEffectTypes, visualItems, visibleGpuEffectTypes],
  )
  const displayItem = visualItems[0] ?? null
  const itemKeyframes = useKeyframesStore(
    useShallow(
      useCallback((s) => itemIds.map((itemId) => s.keyframesByItemId[itemId] ?? null), [itemIds]),
    ),
  )
  const keyframesByItemId = useMemo(() => {
    const map = new Map<string, (typeof itemKeyframes)[number]>()
    for (const [index, itemId] of itemIds.entries()) {
      map.set(itemId, itemKeyframes[index] ?? null)
    }
    return map
  }, [itemIds, itemKeyframes])

  const getMappedEffectEntry = useCallback(
    (item: TimelineItem, displayEffectId: string): ItemEffect | null => {
      return getMappedSelectionEffectEntry(effects, item.effects, displayEffectId)
    },
    [effects],
  )

  const getKeyframeProperty = useCallback(
    (effectId: string, paramKey: string): AnimatableProperty | null => {
      const effect = effects.find((entry) => entry.id === effectId)
      if (!effect || effect.effect.type !== 'gpu-effect') {
        return null
      }

      const definition = getGpuEffect(effect.effect.gpuEffectType)
      const param = definition?.params[paramKey]
      if (!definition || param?.type !== 'number' || !param.animatable) {
        return null
      }

      return buildEffectAnimatableProperty(effect.effect.gpuEffectType, effectId, paramKey)
    },
    [effects],
  )

  const getResolvedDisplayGpuEffect = useCallback(
    (effectEntry: ItemEffect): GpuEffect => {
      const gpuEffect = effectEntry.effect as GpuEffect
      if (!displayItem) {
        return gpuEffect
      }

      const definition = getGpuEffect(gpuEffect.gpuEffectType)
      if (!definition) {
        return gpuEffect
      }

      const itemKeyframeState = keyframesByItemId.get(displayItem.id) ?? undefined
      const relativeFrame = currentFrame - displayItem.from
      let nextParams = gpuEffect.params
      let changed = false

      for (const [paramKey, param] of Object.entries(definition.params)) {
        if (param.type !== 'number' || !param.animatable) {
          continue
        }

        const value = getResolvedAnimatedEffectParamValue(
          effectEntry,
          itemKeyframeState ?? undefined,
          relativeFrame,
          paramKey,
        )
        if (value === null || nextParams[paramKey] === value) {
          continue
        }

        if (!changed) {
          nextParams = { ...gpuEffect.params }
          changed = true
        }

        nextParams[paramKey] = value
      }

      return changed ? { ...gpuEffect, params: nextParams } : gpuEffect
    },
    [currentFrame, displayItem, keyframesByItemId],
  )

  const handleAddGpuEffect = useCallback(
    (gpuEffectId: string) => {
      const defaults = getGpuEffectDefaultParams(gpuEffectId)
      itemIds.forEach((id) => {
        addEffect(id, {
          type: 'gpu-effect',
          gpuEffectType: gpuEffectId,
          params: defaults,
        } as GpuEffect)
      })
    },
    [itemIds, addEffect],
  )

  const gpuCategories = useMemo(
    () =>
      getGpuCategoriesWithEffects()
        .map(({ category, effects: categoryEffects }) => ({
          category,
          effects: categoryEffects.filter((effect) =>
            matchesGpuEffectFilters(effect.id, visibleGpuEffectTypes, hiddenGpuEffectTypes),
          ),
        }))
        .filter(({ effects: categoryEffects }) => categoryEffects.length > 0),
    [hiddenGpuEffectTypes, visibleGpuEffectTypes],
  )

  const triggerPreviews = useCallback(() => prewarmEffectPreviews(), [])
  // Which picker row is hovered — drives that thumbnail's live sweep animation.
  const [hoveredPickerKey, setHoveredPickerKey] = useState<string | null>(null)

  const handleGpuParamChange = useCallback(
    (effectId: string, paramKey: string, value: number | boolean | string) => {
      const effect = effects.find((e) => e.id === effectId)
      if (!effect || effect.effect.type !== 'gpu-effect') return

      const gpuEff = effect.effect as GpuEffect
      const definition = getGpuEffect(gpuEff.gpuEffectType)
      const param = definition?.params[paramKey]
      const autoOperations =
        typeof value === 'number' && definition && param?.type === 'number' && param.animatable
          ? visualItems.flatMap((item) => {
              const targetEffect = getMappedEffectEntry(item, effectId)
              if (!targetEffect || targetEffect.effect.type !== 'gpu-effect') {
                return []
              }

              const itemKeyframeState = keyframesByItemId.get(item.id) ?? undefined
              const property = buildEffectAnimatableProperty(
                targetEffect.effect.gpuEffectType,
                targetEffect.id,
                paramKey,
              )
              const operation = getAutoKeyframeOperation(
                item,
                itemKeyframeState ?? undefined,
                property,
                value,
                currentFrame,
              )
              return operation ? [operation] : []
            })
          : []

      if (autoOperations.length > 0) {
        applyAutoKeyframeOperations(autoOperations)
      }

      visualItems.forEach((item) => {
        const targetEffect = getMappedEffectEntry(item, effectId)
        if (!targetEffect || targetEffect.effect.type !== 'gpu-effect') {
          return
        }

        const property = buildEffectAnimatableProperty(
          targetEffect.effect.gpuEffectType,
          targetEffect.id,
          paramKey,
        )
        const autoHandled =
          typeof value === 'number' &&
          autoOperations.some(
            (operation) => operation.itemId === item.id && operation.property === property,
          )
        if (autoHandled) {
          return
        }

        updateEffect(item.id, targetEffect.id, {
          effect: {
            ...targetEffect.effect,
            params: { ...targetEffect.effect.params, [paramKey]: value },
          },
        })
      })
      queueMicrotask(() => clearPreview())
    },
    [
      applyAutoKeyframeOperations,
      clearPreview,
      currentFrame,
      effects,
      getMappedEffectEntry,
      keyframesByItemId,
      updateEffect,
      visualItems,
    ],
  )

  const handleGpuParamsBatchChange = useCallback(
    (effectId: string, updates: Record<string, number | boolean | string>) => {
      const effect = effects.find((e) => e.id === effectId)
      if (!effect || effect.effect.type !== 'gpu-effect') return

      const gpuEff = effect.effect as GpuEffect
      const definition = getGpuEffect(gpuEff.gpuEffectType)
      const autoOperations = visualItems.flatMap((item) => {
        const targetEffect = getMappedEffectEntry(item, effectId)
        if (!targetEffect || targetEffect.effect.type !== 'gpu-effect') {
          return []
        }

        const itemKeyframeState = keyframesByItemId.get(item.id) ?? undefined

        return Object.entries(updates).flatMap(([paramKey, paramValue]) => {
          const param = definition?.params[paramKey]
          if (
            typeof paramValue !== 'number' ||
            !definition ||
            param?.type !== 'number' ||
            !param.animatable
          ) {
            return []
          }

          const property = buildEffectAnimatableProperty(
            targetEffect.effect.gpuEffectType,
            targetEffect.id,
            paramKey,
          )
          const operation = getAutoKeyframeOperation(
            item,
            itemKeyframeState ?? undefined,
            property,
            paramValue,
            currentFrame,
          )
          return operation ? [operation] : []
        })
      })

      if (autoOperations.length > 0) {
        applyAutoKeyframeOperations(autoOperations)
      }

      visualItems.forEach((item) => {
        const targetEffect = getMappedEffectEntry(item, effectId)
        if (!targetEffect || targetEffect.effect.type !== 'gpu-effect') {
          return
        }

        const fallbackUpdates = { ...updates }
        for (const [paramKey, paramValue] of Object.entries(updates)) {
          const property = buildEffectAnimatableProperty(
            targetEffect.effect.gpuEffectType,
            targetEffect.id,
            paramKey,
          )
          const autoHandled =
            typeof paramValue === 'number' &&
            autoOperations.some(
              (operation) => operation.itemId === item.id && operation.property === property,
            )
          if (autoHandled) {
            delete fallbackUpdates[paramKey]
          }
        }

        if (Object.keys(fallbackUpdates).length === 0) {
          return
        }

        updateEffect(item.id, targetEffect.id, {
          effect: {
            ...targetEffect.effect,
            params: { ...targetEffect.effect.params, ...fallbackUpdates },
          },
        })
      })
      queueMicrotask(() => clearPreview())
    },
    [
      applyAutoKeyframeOperations,
      clearPreview,
      currentFrame,
      effects,
      getMappedEffectEntry,
      keyframesByItemId,
      updateEffect,
      visualItems,
    ],
  )

  const handleGpuParamLiveChange = useCallback(
    (effectId: string, paramKey: string, value: number | boolean | string) => {
      const previews: Record<string, ItemEffect[]> = {}
      visualItems.forEach((item) => {
        const targetEffect = getMappedEffectEntry(item, effectId)
        if (!targetEffect) return
        previews[item.id] = (item.effects ?? []).map((entry) => {
          if (entry.id !== targetEffect.id || entry.effect.type !== 'gpu-effect') return entry
          const entryGpu = entry.effect as GpuEffect
          return {
            ...entry,
            effect: {
              ...entryGpu,
              params: { ...entryGpu.params, [paramKey]: value },
            },
          }
        })
      })
      setEffectsPreviewNew(previews)
    },
    [getMappedEffectEntry, setEffectsPreviewNew, visualItems],
  )

  const handleGpuParamsBatchLiveChange = useCallback(
    (effectId: string, updates: Record<string, number | boolean | string>) => {
      const previews: Record<string, ItemEffect[]> = {}
      visualItems.forEach((item) => {
        const targetEffect = getMappedEffectEntry(item, effectId)
        if (!targetEffect) return
        previews[item.id] = (item.effects ?? []).map((entry) => {
          if (entry.id !== targetEffect.id || entry.effect.type !== 'gpu-effect') return entry
          const entryGpu = entry.effect as GpuEffect
          return {
            ...entry,
            effect: {
              ...entryGpu,
              params: { ...entryGpu.params, ...updates },
            },
          }
        })
      })
      setEffectsPreviewNew(previews)
    },
    [getMappedEffectEntry, setEffectsPreviewNew, visualItems],
  )

  const handleResetGpuEffect = useCallback(
    (effectId: string) => {
      visualItems.forEach((item) => {
        const targetEffect = getMappedEffectEntry(item, effectId)
        if (!targetEffect || targetEffect.effect.type !== 'gpu-effect') {
          return
        }

        const defaults = getGpuEffectDefaultParams(targetEffect.effect.gpuEffectType)
        updateEffect(item.id, targetEffect.id, {
          effect: { ...targetEffect.effect, params: defaults },
        })
      })
    },
    [getMappedEffectEntry, updateEffect, visualItems],
  )

  const handleApplyPreset = useCallback(
    (presetId: string) => {
      const preset = EFFECT_PRESETS.find((p) => p.id === presetId)
      if (!preset) return

      const updates = itemIds.map((id) => ({
        itemId: id,
        effects: preset.effects,
      }))
      addEffects(updates)
    },
    [itemIds, addEffects],
  )

  const handleToggle = useCallback(
    (effectId: string) => {
      visualItems.forEach((item) => {
        const targetEffect = getMappedEffectEntry(item, effectId)
        if (targetEffect) {
          toggleEffect(item.id, targetEffect.id)
        }
      })
    },
    [getMappedEffectEntry, toggleEffect, visualItems],
  )

  const allEffectsEnabled = useMemo(
    () => effects.length > 0 && effects.every((e) => e.enabled),
    [effects],
  )

  const handleToggleAll = useCallback(() => {
    const newEnabled = !allEffectsEnabled
    visualItems.forEach((item) => {
      effects.forEach((effect) => {
        const targetEffect = getMappedEffectEntry(item, effect.id)
        if (targetEffect && targetEffect.enabled !== newEnabled) {
          toggleEffect(item.id, targetEffect.id)
        }
      })
    })
  }, [allEffectsEnabled, effects, getMappedEffectEntry, toggleEffect, visualItems])

  const handleRemove = useCallback(
    (effectId: string) => {
      visualItems.forEach((item) => {
        const targetEffect = getMappedEffectEntry(item, effectId)
        if (targetEffect) {
          removeEffect(item.id, targetEffect.id)
        }
      })
    },
    [getMappedEffectEntry, removeEffect, visualItems],
  )

  const [pickerOpen, setPickerOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({})

  const openPicker = useCallback(() => {
    triggerPreviews()
    setSearchQuery('')
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setPanelStyle({
        position: 'fixed',
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
      })
    }
    setPickerOpen(true)
  }, [triggerPreviews])

  const closePicker = useCallback(() => {
    setPickerOpen(false)
    setSearchQuery('')
    triggerRef.current?.blur()
  }, [])

  useEffect(() => {
    if (!pickerOpen) return
    requestAnimationFrame(() => searchInputRef.current?.focus())
  }, [pickerOpen])

  useEffect(() => {
    if (!pickerOpen) return
    const handlePointerDown = (e: PointerEvent) => {
      if (
        panelRef.current?.contains(e.target as Node) ||
        triggerRef.current?.contains(e.target as Node)
      ) {
        return
      }
      closePicker()
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePicker()
    }
    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [pickerOpen, closePicker])

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return gpuCategories
    const q = searchQuery.toLowerCase()
    return gpuCategories
      .map(({ category, effects: categoryEffects }) => ({
        category,
        effects: categoryEffects.filter((def) => def.name.toLowerCase().includes(q)),
      }))
      .filter(({ effects: categoryEffects }) => categoryEffects.length > 0)
  }, [gpuCategories, searchQuery])

  const filteredPresets = useMemo(() => {
    if (!searchQuery.trim()) return EFFECT_PRESETS
    const q = searchQuery.toLowerCase()
    return EFFECT_PRESETS.filter((preset) => preset.name.toLowerCase().includes(q))
  }, [searchQuery])

  const hasResults = filteredCategories.length > 0 || filteredPresets.length > 0
  const isColorGradeDock =
    layout === 'dock' &&
    visibleGpuEffectTypes?.length === COLOR_GRADE_DOCK_EFFECT_TYPES.length &&
    COLOR_GRADE_DOCK_EFFECT_TYPES.every((type) => visibleGpuEffectTypes.includes(type))

  // Grade panels collapse to a summary row in the sidebar (the Edit workspace);
  // the dock owns the full grading surface, so it never collapses.
  const gradePanelCollapsible = layout !== 'dock' && !!onEditInColor

  if (visualItems.length === 0) return null

  const renderEffectPanel = (effect: ItemEffect) => {
    if (effect.effect.type !== 'gpu-effect') {
      return null
    }

    const gpuEff = effect.effect as GpuEffect
    const def = getGpuEffect(gpuEff.gpuEffectType)
    if (!def) return null
    const displayGpuEffect = getResolvedDisplayGpuEffect(effect)

    if (gpuEff.gpuEffectType === 'gpu-curves') {
      return (
        <GpuCurvesPanel
          key={effect.id}
          effect={effect}
          gpuEffect={displayGpuEffect}
          definition={def}
          layout={layout}
          collapsible={gradePanelCollapsible}
          onEditInColor={onEditInColor}
          onParamChange={handleGpuParamChange}
          onParamLiveChange={handleGpuParamLiveChange}
          onParamsBatchChange={handleGpuParamsBatchChange}
          onParamsBatchLiveChange={handleGpuParamsBatchLiveChange}
          onReset={handleResetGpuEffect}
          onToggle={handleToggle}
          onRemove={handleRemove}
        />
      )
    }

    if (gpuEff.gpuEffectType === 'gpu-color-wheels') {
      return (
        <GpuWheelsPanel
          key={effect.id}
          itemIds={itemIds}
          effect={effect}
          gpuEffect={displayGpuEffect}
          definition={def}
          layout={layout}
          collapsible={gradePanelCollapsible}
          onEditInColor={onEditInColor}
          getKeyframeProperty={getKeyframeProperty}
          onParamChange={handleGpuParamChange}
          onParamLiveChange={handleGpuParamLiveChange}
          onParamsBatchChange={handleGpuParamsBatchChange}
          onParamsBatchLiveChange={handleGpuParamsBatchLiveChange}
          onReset={handleResetGpuEffect}
          onToggle={handleToggle}
          onRemove={handleRemove}
        />
      )
    }

    return (
      <GpuEffectPanel
        key={effect.id}
        itemIds={itemIds}
        effect={effect}
        gpuEffect={displayGpuEffect}
        definition={def}
        getKeyframeProperty={getKeyframeProperty}
        onParamChange={handleGpuParamChange}
        onParamLiveChange={handleGpuParamLiveChange}
        onReset={handleResetGpuEffect}
        onToggle={handleToggle}
        onRemove={handleRemove}
      />
    )
  }

  const wheelsEffect = isColorGradeDock
    ? effects.find(
        (effect) =>
          effect.effect.type === 'gpu-effect' && effect.effect.gpuEffectType === 'gpu-color-wheels',
      )
    : null
  const curvesEffect = isColorGradeDock
    ? effects.find(
        (effect) =>
          effect.effect.type === 'gpu-effect' && effect.effect.gpuEffectType === 'gpu-curves',
      )
    : null

  return (
    <PropertySection title="特效" icon={Sparkles} defaultOpen={true}>
      <div className="flex gap-1 px-2 pb-2">
        <Button
          ref={triggerRef}
          variant="outline"
          size="sm"
          className="h-7 flex-1 text-xs"
          onClick={() => (pickerOpen ? closePicker() : openPicker())}
        >
          <Plus className="mr-1 h-3 w-3" />
          添加特效
        </Button>
        {pickerOpen &&
          createPortal(
            <div
              ref={panelRef}
              style={panelStyle}
              className="z-50 rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 slide-in-from-top-2"
            >
              <div className="border-b p-1.5">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="搜索特效..."
                    className="h-7 w-full rounded-sm border border-input bg-transparent pl-7 pr-2 text-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  />
                </div>
              </div>

              <div className="max-h-[280px] overflow-y-auto overflow-x-hidden p-1">
                {filteredCategories.map(({ category, effects: categoryEffects }, index) => (
                  <div key={category}>
                    {index > 0 && <div className="-mx-1 my-1 h-px bg-muted" />}
                    <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
                      {formatGpuEffectCategory(category)}
                    </div>
                    {categoryEffects.map((def) => (
                      <button
                        key={def.id}
                        type="button"
                        className="relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-xs outline-none hover:bg-accent hover:text-accent-foreground"
                        onMouseEnter={() => setHoveredPickerKey(def.id)}
                        onMouseLeave={() =>
                          setHoveredPickerKey((k) => (k === def.id ? null : k))
                        }
                        onClick={() => {
                          handleAddGpuEffect(def.id)
                          closePicker()
                        }}
                      >
                        <EffectThumbnail
                          effectId={def.id}
                          active={hoveredPickerKey === def.id}
                          className="h-[18px] w-8 flex-shrink-0 rounded-sm"
                        />
                        {translateEffectUiText(def.name)}
                      </button>
                    ))}
                  </div>
                ))}

                {filteredPresets.length > 0 && (
                  <>
                    {filteredCategories.length > 0 && <div className="-mx-1 my-1 h-px bg-muted" />}
                    <div className="px-2 py-1 text-xs font-medium text-muted-foreground">预设</div>
                    {filteredPresets.map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        className="relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-xs outline-none hover:bg-accent hover:text-accent-foreground"
                        onMouseEnter={() => setHoveredPickerKey(`preset:${preset.id}`)}
                        onMouseLeave={() =>
                          setHoveredPickerKey((k) => (k === `preset:${preset.id}` ? null : k))
                        }
                        onClick={() => {
                          handleApplyPreset(preset.id)
                          closePicker()
                        }}
                      >
                        <EffectThumbnail
                          effects={preset.effects}
                          active={hoveredPickerKey === `preset:${preset.id}`}
                          className="h-[18px] w-8 flex-shrink-0 rounded-sm"
                        />
                        {preset.name}
                      </button>
                    ))}
                  </>
                )}

                {!hasResults && (
                  <div className="px-2 py-4 text-center text-xs text-muted-foreground">
                    没有找到特效
                  </div>
                )}
              </div>
            </div>,
            document.body,
          )}
        {effects.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2"
            onClick={handleToggleAll}
            title={allEffectsEnabled ? '禁用全部特效' : '启用全部特效'}
          >
            {allEffectsEnabled ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </Button>
        )}
      </div>

      {isColorGradeDock ? (
        <div className="grid min-h-0 grid-cols-[minmax(0,1.9fr)_minmax(280px,0.72fr)] gap-2 p-2">
          <div className="min-h-[16rem] overflow-hidden rounded-md border border-border/60 bg-background/35">
            {wheelsEffect ? renderEffectPanel(wheelsEffect) : null}
          </div>
          <div className="min-h-[16rem] overflow-hidden rounded-md border border-border/60 bg-background/35">
            {curvesEffect ? renderEffectPanel(curvesEffect) : null}
          </div>
        </div>
      ) : (
        <div className="space-y-0">{effects.map((effect) => renderEffectPanel(effect))}</div>
      )}

      {effects.length === 0 && (
        <div className="px-2 py-3 text-center text-xs text-muted-foreground">
          还没有应用特效。点击“添加特效”开始使用。
        </div>
      )}
    </PropertySection>
  )
})
